import type { FC } from 'react';
import { lazy, Suspense, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChatPage from '../features/chat';
import CenteredSpin from '../components/CenteredSpin';
import AppHeader, { TAB_IDS, type AppTab } from '../features/shell/AppHeader';
import SessionExpiryWarning from '../features/shell/SessionExpiryWarning';
import SignOutDialog from '../features/shell/SignOutDialog';
import { useBreakpoints } from '../features/shell/useViewport';
import PreferencesDialog from '../features/preferences/PreferencesDialog';
import PreferencesSession from '../features/preferences/PreferencesSession';
import styles from '../features/shell/shell.module.css';

// The Guideline・Context screen loads on first open, not with the Q&A page.
const Documents = lazy(() => import('../features/documents'));

/** The signed-in application shell (spec §2): header and the two workspaces. */
const HomePage: FC = () => {
  const { t } = useTranslation();
  const { narrow } = useBreakpoints();
  const [reloadData, setReloadData] = useState<boolean>(false);
  const [tab, setTab] = useState<AppTab>('qa');
  const [contextVisited, setContextVisited] = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const preferencesRef = useRef<HTMLButtonElement>(null);

  const changeTab = (next: AppTab) => {
    if (next === 'ctx') setContextVisited(true);
    setTab(next);
  };

  return (
    <div className={styles.app}>
      <a href="#app-main" className={styles.skipLink}>
        {t('v2.shell.skipToContent')}
      </a>
      <AppHeader
        tab={tab}
        onTab={changeTab}
        onOpenPreferences={() => setPreferencesOpen(true)}
        preferencesOpen={preferencesOpen}
        preferencesRef={preferencesRef}
        onSignOut={() => setSignOutOpen(true)}
      />
      <main id="app-main" className={styles.main} tabIndex={-1}>
        <section
          id={TAB_IDS.qa.panel}
          role="tabpanel"
          aria-labelledby={TAB_IDS.qa.tab}
          hidden={tab !== 'qa'}
          className={styles.panel}
        >
          <ChatPage reloadData={reloadData} />
        </section>
        <section
          id={TAB_IDS.ctx.panel}
          role="tabpanel"
          aria-labelledby={TAB_IDS.ctx.tab}
          hidden={tab !== 'ctx'}
          className={styles.panel}
        >
          {contextVisited && (
            <Suspense fallback={<CenteredSpin variant="fill" />}>
              <Documents
                setReloadData={setReloadData}
                isActive={tab === 'ctx'}
              />
            </Suspense>
          )}
        </section>
      </main>
      <PreferencesDialog
        open={preferencesOpen}
        onClose={() => setPreferencesOpen(false)}
        showSignOut={narrow}
        onSignOut={() => setSignOutOpen(true)}
        returnFocusRef={preferencesRef}
      />
      <SignOutDialog open={signOutOpen} onClose={() => setSignOutOpen(false)} />
      <SessionExpiryWarning />
      <PreferencesSession />
    </div>
  );
};

export default HomePage;
